import React, {
  useState,
  FormEvent,
  ChangeEvent,
  useLayoutEffect,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { adminRoutes } from "../../routers/AdminRouters";
import { GoogleLogin } from "@react-oauth/google";
import { FcGoogle } from "react-icons/fc";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import type { Swiper as SwiperType } from "swiper";

// Use central Assets
import Assets from "../../core/assets";

// Utils
import { useLoginMutation, useLazyGetProfileQuery } from "../../services/admin/admin.rtk.api";
import { setToken } from "../../utils/auth";
import { toast } from "../../core/toast"; // added
import { adminGoogleLogin } from "../../services/admin/adminService.api";

// Add: robust error surface for production (fallback to alert if toast provider missing)
const safeToastError = (msg: string) => {
  try {
    toast.error(msg);
  } catch {
    try {
      console.error(msg);
    } catch { }
    if (typeof window !== "undefined") alert(msg);
  }
};

interface LoginFormData {
  email: string;
  password: string;
}

import { useTheme } from "../../contexts/ThemeContext";

// ... existing interfaces ...

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [login, { isLoading }] = useLoginMutation();
  const [getProfile] = useLazyGetProfileQuery();

  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [errors, setErrors] = useState<Partial<LoginFormData>>({});
  const [activeSlide, setActiveSlide] = useState(0);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof LoginFormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<LoginFormData> = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const res = await login({
        email: formData.email.trim(),
        password: formData.password.trim(),
      }).unwrap();
    } catch (err: any) {
      safeToastError(err?.data?.message || err?.message || "Login failed");
    }
  };



  return (
    <div
      className={`${theme === "dark" ? "dark " : ""
        }min-h-screen flex bg-surface text-text font-sans overflow-hidden`}
    >
      {/* Left Side - Gradient + Swiper */}
      <div
        className={`hidden lg:flex lg:w-1/2 min-h-screen items-center justify-center relative ${theme === "dark"
          ? "bg-[linear-gradient(180deg,_#0E0F10_0%,_#0B0C0D_100%)]"
          : "bg-white"
          } overflow-hidden isolate`} // clip blur + new stacking context
      >
        {theme === "dark" && (
          // Glow overlays
          <div className="pointer-events-none absolute inset-0 z-0">
            {/* Top-left glow */}
            <div className="absolute inset-0 bg-no-repeat bg-[radial-gradient(60.93%_69.29%_at_-7.32%_-12.74%,_#C6D92C_0%,_rgba(15,15,15,0)_100%)] blur-[62px]" />
            {/* Bottom-right glow */}
            <div className="absolute inset-0 bg-no-repeat bg-[radial-gradient(60.93%_69.29%_at_107%_112%,_#C6D92C_0%,_rgba(15,15,15,0)_100%)] blur-[62px]" />
          </div>
        )}

        {/* Swiper Card */}
        <div
          className={`relative z-10 w-[90%] h-[85%] rounded-3xl flex flex-col items-center justify-center shadow-2xl border ${theme === "dark"
            ? "bg-black border-[#232323]"
            : "bg-[#F3F4F1] border-gray-200"
            }`}
        >
          <Swiper
            modules={[Pagination, Autoplay]}
            pagination={false}
            loop
            autoplay={{ delay: 2000, disableOnInteraction: false }}
            slidesPerView={1}
            onSlideChange={(swiper: SwiperType) =>
              setActiveSlide(swiper.realIndex)
            }
            className="w-full h-full flex flex-col items-center justify-center"
            style={{ background: "transparent" }}
          >
            {[0, 1, 2].map((i) => (
              <SwiperSlide key={i}>
                <div className="flex flex-col items-center justify-center h-full">
                  <img
                    src={
                      theme === "dark"
                        ? Assets.authDarkLogo
                        : Assets.authLightLogo
                    }
                    alt="Gym Stream Logo"
                    className="w-32 h-32 object-contain"
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Custom Dots */}
          <div className="flex items-center justify-center gap-2.5 absolute bottom-8 left-0 right-0">
            {[0, 1, 2].map((idx) => (
              <span
                key={idx}
                className={
                  activeSlide === idx
                    ? `rounded-full transition-all duration-300
                       ${theme === "dark" ? "bg-[#B6CB24]" : "bg-[#B6CB24]"}
                       h-2 w-10` // active: lime pill
                    : `rounded-full transition-all duration-300
                       ${theme === "dark" ? "bg-white/40" : "bg-gray-300"}
                       h-2.5 w-2.5` // inactive: small gray dot
                }
              />
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div
        className={`w-full lg:w-1/2 min-h-screen flex items-center justify-center px-8 py-12 ${theme === "dark" ? "bg-surface" : "bg-[#F3F4F1]"
          }`}
      >
        <div className="w-full max-w-md space-y-8 font-sans">
          {/* Theme Toggle */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"
                } theme`}
              title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
              className={`relative p-1 rounded-full transition-all duration-300 flex items-center gap-2 w-24 h-10 ${theme === "dark"
                ? "bg-gray-700 border border-gray-600"
                : "bg-gray-200 border border-gray-300"
                }`}
            >
              {/* Toggle Switch */}
              <div
                className={`absolute w-8 h-8 rounded-full transition-all duration-300 flex items-center justify-center ${theme === "dark"
                  ? "translate-x-14 bg-gray-800"
                  : "translate-x-0 bg-[#B6CB24]"
                  }`}
              >
                {theme === "dark" ? (
                  <img
                    src={Assets.darkModeIcon}
                    alt="Dark Mode"
                    className="w-4 h-4"
                  />
                ) : (
                  <svg
                    className="w-4 h-4 text-gray-800"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414-1.414zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>

              {/* Background Text Labels */}
              <span
                className={`absolute left-2 text-xs font-medium transition-opacity duration-300 ${theme === "dark" ? "opacity-100 text-gray-300" : "opacity-0"
                  }`}
              >
                DARK
              </span>
              <span
                className={`absolute right-2 text-xs font-medium transition-opacity duration-300 ${theme === "light" ? "opacity-100 text-gray-600" : "opacity-0"
                  }`}
              >
                LIGHT
              </span>
            </button>
          </div>

          {/* Header */}
          <div className="text-left">
            <h2
              className={`text-3xl font-bold ${theme === "dark" ? "text-text" : "text-gray-900"
                }`}
            >
              Login
            </h2>
            <p
              className={`mt-2 ${theme === "dark" ? "text-muted" : "text-gray-600"
                }`}
            >
              Login to access your Gymstream account
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className={`block text-sm font-medium mb-2 ${theme === "dark" ? "text-muted" : "text-gray-700"
                  }`}
              >
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                placeholder="user@mail.com"
                className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-colors ${theme === "dark"
                  ? `placeholder-muted focus:ring-brand-500 focus:border-transparent bg-card text-text border-border ${errors.email ? "border-red-500 focus:ring-red-500" : ""
                  }`
                  : `placeholder-gray-400 focus:ring-brand-500 focus:border-brand-500 bg-white text-gray-900 border-gray-300 ${errors.email ? "border-red-500 focus:ring-red-500" : ""
                  }`
                  }`}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-500">{errors.email}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className={`block text-sm font-medium mb-2 ${theme === "dark" ? "text-muted" : "text-gray-700"
                  }`}
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="••••••••••"
                  className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-colors pr-12 ${theme === "dark"
                    ? `placeholder-muted focus:ring-brand-500 focus:border-transparent bg-card text-text border-border ${errors.password
                      ? "border-red-500 focus:ring-red-500"
                      : ""
                    }`
                    : `placeholder-gray-400 focus:ring-brand-500 focus:border-brand-500 bg-white text-gray-900 border-gray-300 ${errors.password
                      ? "border-red-500 focus:ring-red-500"
                      : ""
                    }`
                    }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 focus:outline-none ${theme === "dark"
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                  {showPassword ? (
                    <FaEyeSlash className="w-5 h-5" />
                  ) : (
                    <FaEye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-500">{errors.password}</p>
              )}
            </div>

            {/* Remember & Forgot */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className={`w-4 h-4 rounded focus:ring-2 ${theme === "dark"
                    ? "text-black bg-black border-white focus:ring-brand-500"
                    : "text-brand-600 bg-white border-black focus:ring-brand-500"
                    }`}
                />
                <label
                  htmlFor="remember-me"
                  className={`ml-2 text-sm ${theme === "dark" ? "text-text" : "text-gray-700"
                    }`}
                >
                  Remember me
                </label>
              </div>
              <button
                type="button"
                className="text-sm text-brand-500 hover:text-brand-600 transition-colors font-semibold"
                onClick={() => navigate("/forget-password")}
              >
                Forgot Password
              </button>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full flex items-center justify-center px-4 py-3 rounded-lg border border-[#C6D92C] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-bold focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:ring-offset-surface ${theme === "dark" ? "text-white" : "text-black"
                }`}
              style={{
                background:
                  "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(122, 134, 24, 0.80) 100%)",
              }}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Logging in...
                </div>
              ) : (
                <div className="flex items-center">
                  <img
                    src={Assets.loginArrowIcon}
                    alt="Login Arrow"
                    className={`w-5 h-5 mr-2 ${theme === "light" ? "filter brightness-0" : ""
                      }`}
                  />
                  Login
                </div>
              )}
            </button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div
                  className={`w-full border-t ${theme === "dark" ? "border-border" : "border-gray-200"
                    }`}
                />
              </div>
              <div className="relative flex justify-center text-sm">
                <span
                  className={`px-4 ${theme === "dark"
                    ? "bg-surface text-muted"
                    : "bg-[#F3F4F1] text-gray-500"
                    }`}
                >
                  Or login with
                </span>
              </div>
            </div>

            {/* Google Login (Invisible Overlay) */}
            <div className="relative flex justify-center mt-4">
              {/* Visual Button (Non-functional, purely for design) */}
              <div
                className={`w-12 h-12 flex items-center justify-center border rounded-lg transition-colors p-0 pointer-events-none ${theme === "dark"
                  ? "border-border bg-card"
                  : "border-gray-200 bg-white shadow-sm"
                  }`}
              >
                <FcGoogle className="w-7 h-7" />
              </div>

              {/* Functional Google Button (Invisible & Overlaying) */}
              <div className="absolute inset-0 z-10 opacity-0 overflow-hidden flex items-center justify-center">
                <div style={{ transform: "scale(2)" }}>
                  <GoogleLogin
                    onSuccess={async (res) => {
                      const cred = res?.credential;
                      if (!cred) return;
                      try {
                        const r = await adminGoogleLogin({
                          credential: cred,
                        });
                        if (r?.success && r?.result?.token) {
                          navigate(adminRoutes.dashboard);
                        } else {
                          safeToastError(r?.message || "Google login failed");
                        }
                      } catch (e: any) {
                        safeToastError(
                          e?.data?.message ||
                          e?.message ||
                          "Google login failed"
                        );
                      }
                    }}
                    onError={() => {
                      safeToastError("Google login failed");
                    }}
                    useOneTap={false}
                    theme="outline"
                    type="icon"
                    shape="circle"
                    size="large"
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
